import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { CashRegisterModelType } from '@teinor/erp/types/company/cashRegister'
import { relatedTypesInversed } from '@teinor/erp/types/company/config/paymentType'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import CustomError from '../../../../../../../baseComponents/CustomError'
import { MediumModalCreateEdit } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import TextArea from '../../../../../../../forms/Input/TextArea'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { displayDateWithFormattedHours } from '../../../../../../../utils/globals/date'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { getCompany, getUserInfo } from '../../../../../../../utils/reducers/getters'
import { PaymentTypesPayload } from '../../../../../../../utils/reducers/payments/paymentTypes'
import { PaymentTypesRegisterMoneyTPV } from '../../types'
import CashRegisterMoneyTable from '../CashRegisterMoneyTable'
import '../modalCashRegister.sass'

type ModalOpenCashRegisterProps = ModalEditComponentProps & ModalOpenCashRegisterLogicProps & {
    MyWorkingCenter: TwinDictionary
}

interface CashRegisterModelTypeExtended extends CashRegisterModelType {
    companyName: string
    workingCenterName: string
    workingCenterStationName: string
}

const ModalOpenCashRegister: React.FC<ModalOpenCashRegisterProps & ReduxPaymentTypes> = ({ onSubmit, MyWorkingCenter, MyWorkingCenterStationId, MyWorkingCenterId, paymentTypes, ...rest }) => {
    const { t } = useTwinTranslation()
    const { paymentTypesRegisterMoney, setPaymentTypesRegisterMoney, handleBeforeSubmit, handleOnSubmit } = useModalOpenCashRegisterLogic({MyWorkingCenterStationId, MyWorkingCenterId, onSubmit, paymentTypes })
    const user = getUserInfo()
    const company = getCompany()
    const workingCenterStationsMemorized = useMemo(() => (MyWorkingCenter?.WorkingCenterStations?.length) ? dictionaryComplexFromJsonArr(MyWorkingCenter?.WorkingCenterStations || []) : null, [MyWorkingCenter])

    let extraFields: FormElement<keyof CashRegisterModelTypeExtended>[] = []
    if (MyWorkingCenterStationId) {
        extraFields.push({
            name: 'workingCenterStationName',
            label: t('workStation', 'Puesto'),
            component: 'TextField',
            value: workingCenterStationsMemorized?.[MyWorkingCenterStationId]?.name,
        })
    }
    const fields: FormRender<CashRegisterModelTypeExtended> = [
        {
            cols: 1,
            title: t('company', 'Empresa'),
            elements: [
                {
                    name: 'companyName',
                    label: '',
                    component: 'TextField',
                    value: company?.name,
                },
            ]
        },
        {
            cols: 4,
            title: t('store', 'Tienda'),
            elements: [
                {
                    name: 'workingCenterName',
                    label: t('name','Nombre'),
                    component: 'TextField',
                    value: MyWorkingCenter?.name,
                },
                ...extraFields,
                {
                    name: 'OpenEmployeeId',
                    label: t('employee','Empleado'),
                    component: 'TextField',
                    value: user?.Employee?.fullname_short,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'InputHidden',
                    value: MyWorkingCenterId,
                },
                {
                    name: 'WorkingCenterStationId',
                    component: 'InputHidden',
                    value: MyWorkingCenterStationId || undefined
                },
            ]
        },
        {
            cols: 5,
            title: t('opening', 'Apertura'),
            elements: [
                {
                    name: 'full_assigned_number',
                    label: t('number','Número'),
                    component: 'TextField',
                    value: rest.allRowData?.cashRegister?.full_assigned_number || '-',
                },
                {
                    name: 'createdAt',
                    label: t('openingDate','Fecha de apertura'),
                    component: 'TextField',
                    value: displayDateWithFormattedHours(new Date()),
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: rest.allRowData?.cashRegister?.id
                },
            ]
        },
    ]
    const haveActiveTPVPaymentTypes = paymentTypes?.activeTpv && Object.keys(paymentTypes?.activeTpv)?.length
    return (
        <MediumModalCreateEdit fields={fields} url={'/api/app/order/tpv/cashRegister/createCashRegister'} translations={{
        title: t('openingCashRegister', 'Apertura de caja'),
        button: t('accept', 'Aceptar')
        }} haveButtonPermissions={haveActiveTPVPaymentTypes ? true : false} {...rest} beforeSubmit={handleBeforeSubmit} onSubmit={handleOnSubmit} className='notFlexAutoFormRender'>
            <div className='flex w-full mt-10 '>
                <TextArea label={t('notes', 'Notas')} name='notes' />
            </div>
            {haveActiveTPVPaymentTypes
                ? <CashRegisterMoneyTable {...{ cashRegisterMoneyRows: paymentTypesRegisterMoney?.cashRegisterMoneyRows || [], setPaymentTypesRegisterMoney, isCloseCashRegister: false }} />
                : <CustomError textError={t('dontHaveTpvPaymentTypes', 'No tienes tipos de pago asociados al TPV')} className='mt-20' />}
        </MediumModalCreateEdit>
    )
}

interface ModalOpenCashRegisterLogicProps {
    MyWorkingCenterId?: number
    MyWorkingCenterStationId?: number
    onSubmit: OnSubmit
    paymentTypes: PaymentTypesPayload
}

const useModalOpenCashRegisterLogic = ({ MyWorkingCenterStationId, MyWorkingCenterId, onSubmit, paymentTypes}: ModalOpenCashRegisterLogicProps) => {
    const [paymentTypesRegisterMoney, setPaymentTypesRegisterMoney] = useState<PaymentTypesRegisterMoneyTPV | null>(null)
 
    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const myCashRegisterMoneyRows = []
        for (const element of paymentTypesRegisterMoney?.cashRegisterMoneyRows || []) {
            if (element.endMoney === null) {
                element.endMoney = roundNumberAndDecimals(element.money || 0, totalCalculateParams)
            }
            const diff = parseFloat(String(element.money)) - parseFloat(String(element.endMoney))
            myCashRegisterMoneyRows.push({ ...element,diff, payment_data: vals.notes})
        }
        vals.cashRegisterMoneyRows = myCashRegisterMoneyRows
        if (!vals.WorkingCenterStationId) {
            delete vals.WorkingCenterStationId
        }
        return true
    }, [paymentTypesRegisterMoney?.cashRegisterMoneyRows])

    const handleOnSubmit: OnSubmit = useCallback(async(res, vals) => {
        onSubmit?.(res, vals)
    }, [onSubmit])

    const getLastCashRegisterMoneys = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/tpv/cashRegister/cashRegisterMoney/getLastCashRegisterMoneys', { WorkingCenterId: MyWorkingCenterId, WorkingCenterStationId: MyWorkingCenterStationId })
        const myData = []
        const resultPaymentDict = await dictionaryComplexFromJsonArr(result || {}, 'PaymentTypeId')
        for (const key in paymentTypes?.activeTpv) {
            const myItem = paymentTypes?.activeTpv[key as any]
            if (myItem?.relatedType === relatedTypesInversed['cash']) {
                const subItem = resultPaymentDict?.[myItem.id] || {}
                subItem.money = subItem?.endMoney || subItem?.money || 0
                if (!subItem.PaymentTypeId) {
                    subItem.CurrencyId = myItem?.extraData?.CurrencyId
                    subItem.PaymentTypeId = myItem?.id
                }
                myData.push({...subItem, CashRegisterId: undefined, PaymentType: myItem })
            }
        }
        setPaymentTypesRegisterMoney({ cashRegisterMoneyRows: myData, allPaymentMethodsTotals: [] })
    }, [setPaymentTypesRegisterMoney, MyWorkingCenterStationId, MyWorkingCenterId, paymentTypes?.activeTpv])

    useEffect(() => {
        getLastCashRegisterMoneys()
    }, [getLastCashRegisterMoneys])

    return { paymentTypesRegisterMoney, setPaymentTypesRegisterMoney, handleBeforeSubmit, handleOnSubmit }
}

const paymentTypesDispatch = {
    setPaymentTypes: (payload: PaymentTypesPayload) => ({ type: 'CHANGE_PAYMENTTYPES', payload }),
}

export type ReduxPaymentTypes = ConnectedProps<typeof PaymentTypesConnect>
const mapPaymentTypesConnector = (state: AllReduxPayloads) => ({ paymentTypes: state.paymentTypes })
const PaymentTypesConnect = connect(mapPaymentTypesConnector, paymentTypesDispatch)
const PaymentTypesConnectLoading = withLoading(ModalOpenCashRegister, [{ fetchUrl: '/api/app/config/payment/paymentType/getAllPaymentTypes', propName: 'paymentTypes', setFunctionName: 'setPaymentTypes' }])
const ModalOpenCashRegisterConnect = PaymentTypesConnect(PaymentTypesConnectLoading)

export default ModalOpenCashRegisterConnect